import { View } from 'react-native';
import { useEffect, useState } from 'react';
import usePlatform from '@/utils/hooks/usePlatform';
import useLoader from '@/utils/hooks/useLoader';
import Toast from 'react-native-toast-message';
import { DateTime } from 'luxon';
import { getOrganization, getCurrentOrgID } from '@/data/organization';

const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);

/**
 * ContributionStatementPDF
 *
 * Reusable component that triggers a giving statement PDF export.
 * Renders nothing visible – mounts, generates, exports, then calls onClose.
 *
 * Props:
 *   person        – person object with firstName, lastName, Address, email
 *   transactions  – array of ContributionTransaction objects (fully joined)
 *   startDate     – DateTime | null  (for header display)
 *   endDate       – DateTime | null  (for header display)
 *   onClose       – called after export completes or fails
 */
const ContributionStatementPDF = ({ person, transactions = [], startDate, endDate, onClose }) => {
    const { isWeb } = usePlatform();
    const loader = useLoader();
    const [orgLogoBase64, setOrgLogoBase64] = useState(null);
    const [org, setOrg] = useState(null);
    const [logoReady, setLogoReady] = useState(false);

    useEffect(() => {
        const loadOrg = async () => {
            try {
                const orgID = await getCurrentOrgID();
                const orgData = await getOrganization(orgID);
                setOrg(orgData);

                if (orgData?.logoPublicUrlLongDark || orgData?.logoPublicUrl) {
                    const logoUrl = orgData.logoPublicUrlLongDark || orgData.logoPublicUrl;
                    try {
                        const response = await fetch(logoUrl);
                        const blob = await response.blob();
                        const base64 = await new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.readAsDataURL(blob);
                        });
                        setOrgLogoBase64(base64);
                    } catch {
                        // Logo failed to load, proceed without it
                    }
                }
            } catch {
                // Org load failed
            } finally {
                setLogoReady(true);
            }
        };

        loadOrg();
    }, []);

    const generateHTML = () => {
        const personName = `${person?.firstName || ''} ${person?.lastName || ''}`.trim();
        const personAddress = person?.Address;

        const dateRangeLabel = (() => {
            if (startDate && endDate) {
                return `${DateTime.fromISO(startDate).toLocaleString(DateTime.DATE_MED)} – ${DateTime.fromISO(endDate).toLocaleString(DateTime.DATE_MED)}`;
            }
            return 'All Time';
        })();

        // Sort transactions by date ascending
        const sorted = [...transactions].sort((a, b) =>
            new Date(a.transactionDate) - new Date(b.transactionDate)
        );

        // Totals by fund
        const fundTotals = {};
        sorted.forEach(t => {
            t.Funds?.forEach(f => {
                const name = f.Fund?.name || 'Unassigned';
                fundTotals[name] = (fundTotals[name] || 0) + parseFloat(f.amount || 0);
            });
            if (!t.Funds || t.Funds.length === 0) {
                fundTotals['Unassigned'] = (fundTotals['Unassigned'] || 0) + parseFloat(t.amount || 0);
            }
        });

        const grandTotal = sorted.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

        const transactionRows = sorted.map(t => {
            const date = DateTime.fromISO(t.transactionDate).toLocaleString(DateTime.DATE_MED);
            const txPersonName = `${t.Person?.firstName || ''} ${t.Person?.lastName || ''}`.trim() || personName;
            const method = t.Method?.name || '-';
            const methodDetail = t.methodDetailValue ? `${t.Method?.detailName || ''} ${t.methodDetailValue}`.trim() : (t.Method?.detailName || '-');
            const funds = t.Funds?.length > 0
                ? t.Funds.map(f => `${f.Fund?.name || 'Unassigned'} (${formatCurrency(f.amount)})`).join('<br>')
                : 'Unassigned';
            return `
                <tr>
                    <td>${date}</td>
                    <td>${txPersonName}</td>
                    <td>${method}</td>
                    <td>${methodDetail}</td>
                    <td>${funds}</td>
                    <td class="amount">${formatCurrency(t.amount)}</td>
                </tr>
            `;
        }).join('');

        const fundSummaryRows = Object.entries(fundTotals).map(([name, amount]) => `
            <tr>
                <td>${name}</td>
                <td class="amount">${formatCurrency(amount)}</td>
            </tr>
        `).join('');

        const orgAddressHtml = (() => {
            const a = org?.Address;
            if (!a) return '';
            return `<div style="font-size:12px;color:#6b7280;margin-top:4px;">${a.street}${a.streetLine2 ? `, ${a.streetLine2}` : ''}, ${a.city}, ${a.state} ${a.zip}</div>`;
        })();

        const personAddressHtml = (() => {
            if (!personAddress) return '';
            return `
                <div style="margin-top:4px;font-size:13px;color:#4b5563;">
                    ${personAddress.street}${personAddress.streetLine2 ? `<br>${personAddress.streetLine2}` : ''}<br>
                    ${personAddress.city}, ${personAddress.state} ${personAddress.zip}
                </div>
            `;
        })();

        return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Giving Statement – ${personName}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 12px;
            line-height: 1.5;
            color: #1f2937;
            background: #f5f5f5;
            padding: 20px;
        }
        .page {
            max-width: 820px;
            margin: 0 auto;
            background: white;
            padding: 48px;
            border-radius: 8px;
            box-shadow: 0 2px 12px rgba(0,0,0,0.08);
        }
        /* Header */
        .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding-bottom: 24px;
            border-bottom: 2px solid #6366f1;
            margin-bottom: 28px;
        }
        .org-block { flex: 1; }
        .org-logo { max-height: 50px; max-width: 220px; object-fit: contain; margin-bottom: 8px; display: block; }
        .org-name { font-size: 20px; font-weight: 700; color: #1f2937; }
        .statement-title-block { text-align: right; }
        .statement-title { font-size: 22px; font-weight: 700; color: #6366f1; }
        .statement-subtitle { font-size: 12px; color: #9ca3af; margin-top: 4px; }
        /* Address block */
        .address-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 32px;
            gap: 40px;
        }
        .address-block { flex: 1; }
        .address-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #9ca3af; font-weight: 600; margin-bottom: 6px; }
        .address-name { font-size: 15px; font-weight: 700; color: #1f2937; }
        /* Table */
        .section-title {
            font-size: 13px;
            font-weight: 700;
            color: #6366f1;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 12px;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 32px;
            font-size: 12px;
        }
        thead tr { background: #6366f1; color: white; }
        thead th {
            padding: 10px 12px;
            text-align: left;
            font-weight: 600;
            font-size: 11px;
            letter-spacing: 0.3px;
        }
        thead th.amount { text-align: right; }
        tbody tr:nth-child(even) { background: #f9fafb; }
        tbody tr:hover { background: #f0f0ff; }
        tbody td { padding: 9px 12px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
        tbody td.amount { text-align: right; font-weight: 600; color: #16a34a; white-space: nowrap; }
        .no-data { padding: 20px; text-align: center; color: #9ca3af; font-style: italic; }
        /* Summary */
        .summary-table { width: 320px; margin-left: auto; margin-bottom: 32px; }
        .summary-table thead tr { background: #374151; }
        .summary-table tbody td { border-bottom: 1px solid #e5e7eb; }
        .grand-total-row { background: #eff6ff !important; }
        .grand-total-row td { font-size: 14px; font-weight: 700; color: #1f2937; border-top: 2px solid #6366f1; }
        .grand-total-row td.amount { color: #16a34a; }
        /* Footer */
        .footer {
            margin-top: 40px;
            padding-top: 16px;
            border-top: 1px solid #e5e7eb;
            font-size: 10px;
            color: #9ca3af;
            text-align: center;
        }
        @media print {
            body { background: white; padding: 0; }
            .page { box-shadow: none; padding: 24px; border-radius: 0; }
        }
    </style>
</head>
<body>
<div class="page">
    <!-- Header -->
    <div class="header">
        <div class="org-block">
            ${orgLogoBase64
                ? `<img src="${orgLogoBase64}" class="org-logo" alt="${org?.name || 'Organization'}" />`
                : `<div class="org-name">${org?.name || 'Organization'}</div>`
            }
            ${orgAddressHtml}
        </div>
        <div class="statement-title-block">
            <div class="statement-title">Giving Statement</div>
            <div class="statement-subtitle">${dateRangeLabel}</div>
            <div class="statement-subtitle" style="margin-top:8px;">Generated ${DateTime.now().toLocaleString(DateTime.DATE_MED)}</div>
        </div>
    </div>

    <!-- Address Block -->
    <div class="address-row">
        <div class="address-block">
            <div class="address-label">Prepared for</div>
            <div class="address-name">${personName}</div>
            ${personAddressHtml}
            ${person?.email ? `<div style="font-size:12px;color:#6b7280;margin-top:4px;">${person.email}</div>` : ''}
        </div>
        <div class="address-block" style="text-align:right;">
            <div class="address-label">Tax Year / Period</div>
            <div style="font-size:14px;font-weight:600;color:#1f2937;">${dateRangeLabel}</div>
        </div>
    </div>

    <!-- Transactions Table -->
    <div class="section-title">Transactions</div>
    <table>
        <thead>
            <tr>
                <th>Date</th>
                <th>Name</th>
                <th>Method</th>
                <th>Method Detail</th>
                <th>Fund</th>
                <th class="amount">Amount</th>
            </tr>
        </thead>
        <tbody>
            ${sorted.length > 0 ? transactionRows : `<tr><td colspan="6" class="no-data">No transactions found for this period.</td></tr>`}
        </tbody>
    </table>

    <!-- Summary by Fund -->
    <div class="section-title">Summary by Fund</div>
    <table class="summary-table">
        <thead>
            <tr>
                <th>Fund</th>
                <th class="amount">Total</th>
            </tr>
        </thead>
        <tbody>
            ${fundSummaryRows}
            <tr class="grand-total-row">
                <td>Grand Total</td>
                <td class="amount">${formatCurrency(grandTotal)}</td>
            </tr>
        </tbody>
    </table>

    <div class="footer">
        This statement is provided for your records. Please retain for tax purposes. &bull;
        Generated by Reach CHMS on ${DateTime.now().toLocaleString(DateTime.DATETIME_MED)}
    </div>
</div>
</body>
</html>
        `;
    };

    const handleExport = async () => {
        const fileName = `${person?.firstName || 'Giving'}_${person?.lastName || 'Statement'}_Statement`;

        if (isWeb) {
            const html = generateHTML();
            const printWindow = window.open('', '_blank');
            printWindow.document.write(html);
            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => {
                printWindow.print();
                onClose?.();
            }, 500);
        } else {
            try {
                loader.enable();
                const html = generateHTML();

                let RNHTMLtoPDF;
                try {
                    RNHTMLtoPDF = await import('react-native-html-to-pdf');
                } catch {
                    throw new Error('PDF library not available');
                }

                const { shareAsync } = await import('expo-sharing');

                const file = await RNHTMLtoPDF.generatePDF({
                    html,
                    fileName,
                    directory: 'Documents',
                });

                loader.disable();
                onClose?.();

                if (file?.filePath) {
                    try {
                        await shareAsync(file.filePath, {
                            mimeType: 'application/pdf',
                            dialogTitle: `Share ${person?.firstName}'s Giving Statement`,
                        });
                    } catch {
                        // User cancelled share
                    }
                }

                Toast.show({ type: 'success', text2: 'Statement exported successfully' });
            } catch (error) {
                loader.disable();
                Toast.show({ type: 'error', text2: 'Failed to export statement: ' + error.message });
                onClose?.();
            }
        }
    };

    useEffect(() => {
        if (logoReady) {
            handleExport();
        }
    }, [logoReady]);

    return <View style={{ display: 'none' }} />;
};

export default ContributionStatementPDF;
